import { useState } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon, CheckCircle2, Shield, ChevronRight } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { QUOTE } from "@/constants/testIds";
import { cn } from "@/lib/utils";

const SERVICES = [
  "Professional Security Officers",
  "Mobile Security Patrol",
  "Day & Night Patrol",
  "Event Security",
  "Commercial Property Security",
  "Residential Security",
  "Construction Site Security",
  "Parking Lot Patrol",
  "Emergency Security Coverage",
  "Custom Security Plans",
];

const initial = {
  first_name: "", last_name: "", phone: "", email: "",
  company_name: "", service_needed: "", message: "",
};

export default function GetQuote() {
  const [form, setForm] = useState(initial);
  const [date, setDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const [emailDelivered, setEmailDelivered] = useState(null);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service_needed) {
      toast.error("Please select a service");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/quote", {
        ...form,
        preferred_date: date ? date.toISOString() : null,
      });
      setEmailDelivered(!!res.data?.email_delivered);
      setSuccess(true);
      setForm(initial);
      setDate(null);
      toast.success("Quote request sent. We'll be in touch shortly.");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let msg = "Something went wrong. Please try again, or call us at 206-742-9195.";
      if (status === 422) msg = "Please check your details — some fields are missing or invalid.";
      else if (typeof detail === "string") msg = detail;
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#000000] text-white">
      <section className="py-20 md:py-28 border-b border-white/8 relative">
        <div className="absolute inset-0 grid-lines-navy opacity-30" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-start">
          {/* Left */}
          <div>
            <p className="font-condensed text-xs font-bold tracking-[0.28em] uppercase text-red-500 mb-3">— Get Quote</p>
            <h1 className="font-display font-black text-4xl md:text-5xl lg:text-6xl uppercase leading-[1] text-balance">
              Request a Tailored<br />Security Quote<span className="text-red-500">.</span>
            </h1>
            <p className="mt-6 text-slate-400 leading-relaxed">
              Tell us about your site, event, or business. We'll respond with a plan built around your needs —
              typically within one business day.
            </p>

            <ul className="mt-8 space-y-3.5">
              {[
                "Licensed & insured across Washington State",
                "Custom plans for your schedule & property",
                "Trained, uniformed officers",
                "Rapid response and clear communication",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3 text-slate-200">
                  <div className="w-5 h-5 rounded-sm bg-red-600 flex items-center justify-center shrink-0 mt-0.5">
                    <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                  </div>
                  {b}
                </li>
              ))}
            </ul>

            <div className="mt-10 flex items-center gap-3 text-slate-400 text-sm border-t border-white/10 pt-6">
              <Shield className="w-5 h-5 text-red-500" />
              <span className="font-condensed uppercase tracking-widest text-xs">Serving King · Pierce County & Surrounding Areas</span>
            </div>
          </div>

          {/* Right */}
          <div className="bg-[#0A0A0A]/80 backdrop-blur-xl border border-white/10 rounded-sm p-6 sm:p-8">
            {success ? (
              <div data-testid={QUOTE.successAlert} className="text-center py-8">
                <div className="w-14 h-14 rounded-sm bg-red-600 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-display font-black text-2xl uppercase">Request Received.</h3>
                <p className="mt-3 text-slate-400">
                  Thank you — your quote request has been saved. Our team will review it and contact you shortly.
                </p>
                <div className="mt-5 inline-flex items-center gap-2 text-xs font-condensed uppercase tracking-widest text-slate-500 border border-white/10 rounded-sm px-3 py-2">
                  <Shield className="w-3.5 h-3.5 text-red-500" />
                  {emailDelivered
                    ? "Notification sent to info@marcopolosecurity.com"
                    : "Saved · Our team has been notified"}
                </div>
                <button
                  onClick={() => { setSuccess(false); setEmailDelivered(null); }}
                  className="mt-6 block mx-auto font-condensed text-sm uppercase tracking-widest text-red-500 hover:text-red-400 border-b border-red-600"
                  data-testid="quote-submit-another"
                >
                  Submit Another Request
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="First Name" required>
                    <input required value={form.first_name} onChange={upd("first_name")} data-testid={QUOTE.firstName} className={inputCls} />
                  </Field>
                  <Field label="Last Name" required>
                    <input required value={form.last_name} onChange={upd("last_name")} data-testid={QUOTE.lastName} className={inputCls} />
                  </Field>
                  <Field label="Phone Number" required>
                    <input required type="tel" value={form.phone} onChange={upd("phone")} data-testid={QUOTE.phone} className={inputCls} />
                  </Field>
                  <Field label="Email Address" required>
                    <input required type="email" value={form.email} onChange={upd("email")} data-testid={QUOTE.email} className={inputCls} />
                  </Field>
                </div>

                <Field label="Company Name">
                  <input value={form.company_name} onChange={upd("company_name")} data-testid={QUOTE.company} className={inputCls} />
                </Field>

                <Field label="Service Needed" required>
                  <Select value={form.service_needed} onValueChange={(v) => setForm({ ...form, service_needed: v })}>
                    <SelectTrigger data-testid={QUOTE.service} className="bg-[#000000] border-white/15 text-white h-11">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/15 text-white">
                      {SERVICES.map((s) => (
                        <SelectItem key={s} value={s} className="focus:bg-white/10">{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field label="Preferred Date">
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-testid={QUOTE.date}
                        className={cn(inputCls, "w-full text-left flex items-center justify-between")}
                      >
                        <span className={date ? "text-white" : "text-slate-500"}>
                          {date ? format(date, "PPP") : "Pick a date"}
                        </span>
                        <CalendarIcon className="w-4 h-4 text-slate-400" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-[#0A0A0A] border-white/15" align="start">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        initialFocus
                        disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}
                      />
                    </PopoverContent>
                  </Popover>
                </Field>

                <Field label="Message / Security Details">
                  <textarea
                    rows={4}
                    value={form.message}
                    onChange={upd("message")}
                    data-testid={QUOTE.message}
                    className={cn(inputCls, "resize-none")}
                    placeholder="Tell us about your property, event, or needs…"
                  />
                </Field>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid={QUOTE.submit}
                  className="group w-full bg-white hover:bg-slate-100 disabled:opacity-60 text-[#0A0A0A] px-6 py-3.5 rounded-sm font-condensed uppercase tracking-wider font-bold transition-colors flex items-center justify-center gap-2"
                >
                  {submitting ? "Sending…" : (<>Send Quote Request <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" /></>)}
                </button>

                <p className="text-xs text-slate-500 text-center font-condensed uppercase tracking-widest">
                  By Submitting You Agree to Be Contacted
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

const inputCls =
  "w-full bg-[#000000] border border-white/15 rounded-sm px-4 py-3 h-11 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent transition-all";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block font-condensed text-xs font-bold uppercase tracking-widest text-slate-300 mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </span>
      {children}
    </label>
  );
}
